import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { Link } from "react-router-dom";
import API_BASE from "../config/apiBase";
import { getMetroRequestHeaders } from "../services/metroEligibility";
import { readSavedVerifiedAddresses, saveVerifiedAddresses } from "../services/verifiedAddressStorage";
import { getPublishedVerifiedAddresses } from "../services/publicVerifiedAddresses";
import "./VerifiedAddressPanel.css";

const VERIFIED_API = `${API_BASE}/api/metro/verified`;
const LOCAL_API_ENABLED = typeof window !== "undefined"
  && ["localhost", "127.0.0.1", "::1", "[::1]"].includes(window.location.hostname);
const validRow = (row) => ["Metro", "T-Mobile"].includes(row?.provider) && row.status === "eligible"
  && typeof row.address === "string" && Boolean(row.address.trim()) && /^\d{5}$/.test(row.zipCode)
  && typeof row.referenceId === "string" && Boolean(row.referenceId.trim())
  && typeof row.checkedAt === "string" && Number.isFinite(Date.parse(row.checkedAt));
const errorText = (error, fallback) => error.response?.status === 401 || error.code === "METRO_AUTH_REQUIRED"
  ? "Please sign in again to view verified addresses."
  : typeof error.response?.data?.message === "string" ? error.response.data.message : fallback;

const cachedPage = (items, zipCode, search) => {
  const q = search.toLowerCase();
  const filtered = items.filter(row => (!zipCode || row.zipCode === zipCode)
    && (!q || `${row.address} ${row.zipCode} ${row.provider}`.toLowerCase().includes(q)));
  return { items: filtered, total: filtered.length, zipCodes: [...new Set([...items.map(row => row.zipCode), ...(zipCode ? [zipCode] : [])])].sort() };
};
const savedCsv = rows => {
  const cell = value => {
    let text = String(value ?? "");
    // eslint-disable-next-line no-control-regex -- Spreadsheet apps can ignore ASCII controls before a formula.
    if (/^[\u0000-\u0020]*[=+\-@]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  return `\uFEFF${[
    ["Provider", "Address", "ZIP code", "Eligibility", "Verified time UTC", "Eligibility reference", "Source URL"],
    ...rows.map(row => [row.provider, row.address, row.zipCode, "Eligible", row.checkedAt, row.referenceId, row.sourceUrl]),
  ].map(row => row.map(cell).join(",")).join("\r\n")}\r\n`;
};

const VerifiedAddressPanel = ({ userId, useLocalApi = LOCAL_API_ENABLED }) => {
  const id = useId();
  const [view, setView] = useState(null);
  const [zipCode, setZipCode] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(Boolean(userId));
  const [loadError, setLoadError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [notice, setNotice] = useState("");
  const [storageNotice, setStorageNotice] = useState("");
  const [storageError, setStorageError] = useState("");
  const mounted = useRef(false);
  const loadRequest = useRef(null);
  const exportRequest = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; loadRequest.current?.abort(); exportRequest.current?.abort(); };
  }, []);

  useEffect(() => {
    if (searchInput.trim() === search) return;
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const load = useCallback(async () => {
    if (!userId || !mounted.current) return;
    loadRequest.current?.abort();
    const controller = new AbortController();
    loadRequest.current = controller;
    const current = () => mounted.current && !controller.signal.aborted && loadRequest.current === controller;
    setLoading(true); setLoadError("");
    const showResults = (data, source, saveItems = data.items) => {
      if (!current()) return;
      setView({ userId, data, source });
      try {
        const saved = saveVerifiedAddresses(userId, saveItems, { replace: true });
        setStorageNotice(source === "published" ? "Showing the saved verified address list."
          : `${saved.length.toLocaleString()} addresses saved in this browser.`);
        setStorageError("");
      } catch (_error) {
        setStorageNotice("");
        setStorageError("Addresses loaded, but could not be saved in this browser. Allow browser storage or free up space, then refresh.");
      }
    };
    const showPublished = () => {
      const items = getPublishedVerifiedAddresses();
      if (!Array.isArray(items) || items.some(row => !validRow(row))) throw new Error("Invalid saved verified-address list.");
      showResults(cachedPage(items, "", ""), "published");
    };
    let cached = [];
    try {
      cached = readSavedVerifiedAddresses(userId);
      if (cached.length && current()) {
        setView({ userId, data: cachedPage(cached, "", ""), source: "local" });
        setStorageNotice(`${cached.length.toLocaleString()} addresses saved in this browser.`);
      }
    } catch (_error) { /* The API may still provide a valid list if browser storage is unavailable. */ }
    try {
      if (!useLocalApi) {
        showPublished();
        return;
      }
      // Keep one complete catalog so filtering cannot shrink the ZIP summary.
      const { data } = await axios.get(VERIFIED_API, {
        headers: await getMetroRequestHeaders(), signal: controller.signal, timeout: 30000,
      });
      if (!current()) return;
      if (!Array.isArray(data?.items) || data.items.some((row) => !validRow(row))
        || !Number.isSafeInteger(data.total) || data.total !== data.items.length
        || !Array.isArray(data.zipCodes) || data.zipCodes.some((zip) => typeof zip !== "string" || !/^\d{5}$/.test(zip))) {
        throw new Error("Invalid verified-address response.");
      }
      showResults(data, "api");
    } catch (error) {
      if (current()) {
        if (cached.length) {
          setView({ userId, data: cachedPage(cached, "", ""), source: "local" });
          setStorageNotice("Showing addresses saved in this browser. Refresh when the address service is available.");
        } else if (useLocalApi) {
          try { showPublished(); }
          catch (_failure) { setLoadError(errorText(error, "Could not load saved addresses. Please refresh to retry.")); }
        } else setLoadError("Could not load saved addresses. Please refresh to retry.");
      }
    } finally {
      if (current()) { loadRequest.current = null; setLoading(false); }
    }
  }, [userId, useLocalApi]);

  useEffect(() => { void load(); return () => loadRequest.current?.abort(); }, [load]);

  useEffect(() => {
    if (!userId || !useLocalApi) return undefined;
    const refresh = setInterval(() => {
      if (document.visibilityState === "visible" && !loadRequest.current) void load();
    }, 30000);
    return () => clearInterval(refresh);
  }, [userId, useLocalApi, load]);

  const currentView = Boolean(userId) && view?.userId === userId;
  const allRows = currentView ? view.data.items : [];
  const data = currentView ? { ...cachedPage(allRows, zipCode, search), zipCodes: view.data.zipCodes } : null;
  const rows = data?.items || [];
  const zipCounts = [...allRows.reduce((counts, row) => counts.set(row.zipCode, (counts.get(row.zipCode) || 0) + 1), new Map())]
    .sort(([left], [right]) => left.localeCompare(right));
  const busy = loading || (Boolean(userId) && !currentView && !loadError);
  const selectSummaryZip = (value) => {
    setZipCode(value); setSearchInput(""); setSearch(""); setNotice(""); setExportError("");
  };

  const download = async () => {
    if (!userId || !currentView || !rows.length || exportRequest.current || !mounted.current) return;
    const controller = new AbortController();
    exportRequest.current = controller;
    const current = () => mounted.current && !controller.signal.aborted && exportRequest.current === controller;
    setExporting(true); setExportError(""); setNotice("");
    try {
      if (view.source !== "api") {
        saveAs(new Blob([savedCsv(rows)], { type: "text/csv;charset=utf-8" }), `verified-addresses${zipCode ? `-${zipCode}` : ""}.csv`);
        setNotice("CSV downloaded from saved addresses.");
        return;
      }
      const response = await axios.get(`${VERIFIED_API}/export`, {
        params: { ...(zipCode ? { zipCode } : {}), ...(search ? { q: search } : {}) }, responseType: "blob",
        headers: await getMetroRequestHeaders(), signal: controller.signal, timeout: 30000,
      });
      if (!current()) return;
      if (!(response.data instanceof Blob) || !/^text\/csv\b/i.test(response.headers["content-type"] || "")) throw new Error("Invalid CSV response.");
      saveAs(response.data, `verified-addresses${zipCode ? `-${zipCode}` : ""}.csv`);
      setNotice("CSV downloaded.");
    } catch (error) {
      let message = errorText(error, "Could not download CSV. Please retry.");
      if (error.response?.data instanceof Blob && error.response.status !== 401) {
        try { const body = JSON.parse(await error.response.data.text()); if (typeof body.message === "string") message = body.message; } catch (_) { /* Retain the download error. */ }
      }
      if (current()) setExportError(message);
    } finally {
      if (current()) { exportRequest.current = null; setExporting(false); }
    }
  };

  return (
    <div className="verified-list">
      <header className="verified-list-heading">
        <div className="verified-list-title"><h1 id="verified-addresses-title">Verified addresses</h1>{currentView && <span className="verified-list-count" aria-live="polite">{rows.length.toLocaleString()} addresses</span>}</div>
        <Link className="verified-list-open" to="/get-address">Get Address</Link>
      </header>
      {!userId ? <p className="verified-list-feedback">Sign in to view verified addresses.</p> : <>
        {currentView && <section className="verified-list-summary" aria-labelledby={`${id}-summary-title`}>
          <div className="verified-list-summary-heading">
            <h2 id={`${id}-summary-title`}>Verified addresses by ZIP</h2>
            <p>Counts cover all saved verified addresses. Select a ZIP to view its addresses.</p>
          </div>
          <ul className="verified-list-summary-grid" aria-label="Verified address counts by ZIP code">
            <li><button type="button" aria-pressed={!zipCode} onClick={() => selectSummaryZip("")} aria-label={`All ZIP codes, ${allRows.length} verified ${allRows.length === 1 ? "address" : "addresses"}`}><span>All ZIP codes</span><strong>{allRows.length.toLocaleString()}</strong></button></li>
            {zipCounts.map(([zip, count]) => <li key={zip}><button type="button" aria-pressed={zipCode === zip} onClick={() => selectSummaryZip(zip)} aria-label={`ZIP ${zip}, ${count} verified ${count === 1 ? "address" : "addresses"}`}><span>{zip}</span><strong>{count.toLocaleString()}</strong></button></li>)}
          </ul>
        </section>}
        <div className="verified-list-toolbar">
          <div className="verified-list-zip"><label htmlFor={`${id}-zip`}>ZIP code</label><select id={`${id}-zip`} value={zipCode} onChange={(event) => { setZipCode(event.target.value); setNotice(""); setExportError(""); }}><option value="">All ZIP codes</option>{data?.zipCodes.map((zip) => <option key={zip} value={zip}>{zip}</option>)}</select></div>
          <div className="verified-list-search"><label htmlFor={`${id}-search`}>Search addresses</label><input id={`${id}-search`} type="search" value={searchInput} maxLength={200} placeholder="Street, address, or ZIP code" onChange={(event) => setSearchInput(event.target.value)} /></div>
          <button className="verified-list-download" type="button" onClick={download} disabled={exporting || !currentView || !rows.length}>{exporting ? "Downloading…" : "Download CSV"}</button>
          <button className="verified-list-refresh" type="button" onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
        </div>
        {loadError && <p className="verified-list-error" role="alert">{loadError}</p>}
        {storageError && <p className="verified-list-error" role="alert">{storageError}</p>}
        {storageNotice && <p className="verified-list-feedback" role="status">{storageNotice}</p>}
        {exportError && <p className="verified-list-error" role="alert">{exportError}</p>}
        {notice && <p className="verified-list-notice" role="status">{notice}</p>}
        <div className="verified-list-scroll" role="region" aria-label="All verified addresses" aria-busy={busy} tabIndex={0}>
          <table>
            <caption className="verified-list-sr-only">All matching confirmed addresses with ZIP code, provider, eligibility and verification date</caption>
            <thead><tr><th scope="col">Address</th><th scope="col">ZIP code</th><th scope="col">Provider</th><th scope="col">Eligibility</th><th scope="col">Verified on</th></tr></thead>
            <tbody>{rows.length ? rows.map((row) => <tr key={row.id || `${row.provider}-${row.referenceId}`}>
              <th scope="row">{row.address}</th><td>{row.zipCode}</td><td>{row.provider === "T-Mobile" ? "T-Mobile 5G" : "Metro"}</td>
              <td><span className="verified-list-eligible" title={`Reference: ${row.referenceId}`}>Verified eligible</span></td>
              <td><time dateTime={row.checkedAt} title={row.checkedAt}>{new Date(row.checkedAt).toLocaleString()}</time></td>
            </tr>) : <tr><td colSpan={5} className="verified-list-empty" role={busy ? "status" : undefined}>{busy ? "Loading verified addresses…" : loadError ? "Refresh to load the address list." : zipCode || search ? "No verified addresses match these filters." : "No verified addresses saved yet."}</td></tr>}</tbody>
          </table>
        </div>
      </>}
    </div>
  );
};

export default VerifiedAddressPanel;
